import { useRef, useState } from "react";
import { getBusinessProfile } from "../../../services/dashboardApi";
import { useLang } from "../../../core/lang";
import LangKeys from "../../../core/locales/keys";
import { compressImage } from "../../../core/imageUtils";

function UploadLogoPage({ setActivePage }) {
  const { t } = useLang();

  const fileRef = useRef(null);
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const currentLogo =
    JSON.parse(localStorage.getItem("business_profile") || "null")?.logo_url || "";

  const pickImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > 5) {
      setImage(null);
      setPreview("");
      setError(t(LangKeys.photo_size_error));
      return;
    }

    const allowed = ["image/jpeg", "image/png", "image/webp"];
    if (!allowed.includes(file.type)) {
      setImage(null);
      setPreview("");
      setError(t(LangKeys.invalid_image_format));
      return;
    }

    try {
      const compressed = await compressImage(file);
      setImage(compressed);
      setPreview(URL.createObjectURL(compressed));
      setError("");
    } catch (err) {
      setImage(file);
      setPreview(URL.createObjectURL(file));
      setError("");
    }
  };

  const saveLogo = async () => {
    if (!image) {
      setError(t(LangKeys.choose_photo));
      return;
    }

    try {
      setSaving(true);

      const token = localStorage.getItem("token");
      const form = new FormData();
      form.append("logo", image);

      const res = await fetch(
        "https://khata.moxpal.com/api/v2/index.php/business-profile/logo",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: form,
        }
      );

      const data = await res.json();

      if (!data?.success) {
        throw new Error(data?.error?.message || t(LangKeys.upload_error));
      }

      const profileRes = await getBusinessProfile();
      localStorage.setItem(
        "business_profile",
        JSON.stringify(profileRes?.data || {})
      );

      setActivePage(null);
    } catch (err) {
      setError(err.message || t(LangKeys.upload_error));
    } finally {
      setSaving(false);
    }
  };

  const avatarSrc = preview || currentLogo;

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <button style={styles.backBtn} onClick={() => setActivePage(null)}>
          ←
        </button>
        <h2 style={styles.title}>{t(LangKeys.business_logo)}</h2>
      </div>

      <div style={styles.card}>
        <div style={styles.avatarWrap}>
          {avatarSrc ? (
            <img src={avatarSrc} alt="Logo" style={styles.avatar} />
          ) : (
            <div style={styles.placeholder}>🏪</div>
          )}
        </div>

        {error ? <div style={styles.error}>{error}</div> : null}

        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={pickImage}
          style={{ display: "none" }}
        />

        <button style={styles.chooseBtn} onClick={() => fileRef.current?.click()}>
          {t(LangKeys.choose_photo)}
        </button>

        <button
          style={{
            ...styles.saveBtn,
            ...(image && !saving ? {} : styles.saveBtnDisabled),
          }}
          onClick={saveLogo}
          disabled={!image || saving}
        >
          {saving ? t(LangKeys.saving) : t(LangKeys.save_logo)}
        </button>
      </div>
    </div>
  );
}

const styles = {
  page: {
    padding: "16px",
  },

  header: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "16px",
  },

  backBtn: {
    width: "40px",
    height: "40px",
    borderRadius: "12px",
    border: "1px solid #E5E7EB",
    background: "#fff",
    cursor: "pointer",
    fontSize: "20px",
  },

  title: {
    fontSize: "20px",
    fontWeight: "700",
    color: "#111827",
    margin: 0,
  },

  card: {
    background: "#fff",
    borderRadius: "20px",
    padding: "24px",
    border: "1px solid #E5E7EB",
    maxWidth: "520px",
  },

  avatarWrap: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "14px",
  },

  avatar: {
    width: "96px",
    height: "96px",
    borderRadius: "50%",
    objectFit: "cover",
  },

  placeholder: {
    width: "96px",
    height: "96px",
    borderRadius: "50%",
    background: "#E5E7EB",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "42px",
  },

  error: {
    textAlign: "center",
    color: "#DC2626",
    fontSize: "14px",
    marginBottom: "12px",
  },

  chooseBtn: {
    width: "100%",
    height: "46px",
    border: "none",
    borderRadius: "14px",
    background: "#E0E7FF",
    color: "#1E40AF",
    fontWeight: "600",
    cursor: "pointer",
    marginBottom: "12px",
  },

  saveBtn: {
    width: "100%",
    height: "48px",
    border: "none",
    borderRadius: "14px",
    background: "#2563EB",
    color: "#fff",
    fontWeight: "700",
    cursor: "pointer",
  },

  saveBtnDisabled: {
    background: "#93C5FD",
    cursor: "not-allowed",
  },
};

export default UploadLogoPage;